import React, { useState, useEffect } from "react";
import {
  Modal,
  Form,
  Input,
  Tag,
  Alert,
  Typography,
  message,
  Spin,
} from "antd";
import { IndoorPositioning } from "../../api";

// Fallbacks only; the server reports its real defaults on the main record.
const FALLBACK_BROKER = "mqtt.bconimg.com:1883";
const FALLBACK_TOPIC = "lora-pub";

const STATUS_CONFIG = {
  已連線: {
    color: "green",
    label: "已連線",
    description: "MQTT已成功連線至Broker",
  },
  連線中: {
    color: "orange",
    label: "連線中",
    description: "正在嘗試連線至Broker...",
  },
  未連線: {
    color: "default",
    label: "未連線",
    description: "尚未建立MQTT連線",
  },
};

const MqttModal = ({ open, onClose, machineID }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [mqttStatus, setMqttStatus] = useState("未連線");
  const [usingDefaults, setUsingDefaults] = useState(true);
  const [defaultBroker, setDefaultBroker] = useState(FALLBACK_BROKER);
  const [defaultTopic, setDefaultTopic] = useState(FALLBACK_TOPIC);

  useEffect(() => {
    if (open && machineID) {
      loadConfig();
    }
  }, [open, machineID]);

  const loadConfig = async () => {
    setLoading(true);
    try {
      const response = await IndoorPositioning.GetIndoorPositioning(machineID);
      if (response.code === 200) {
        const broker = response.data?.mqtt_broker_address || "";
        const topic = response.data?.mqtt_topic || "";
        form.setFieldsValue({ mqtt_broker_address: broker, mqtt_topic: topic });
        setMqttStatus(response.data?.mqtt_status || "未連線");
        setDefaultBroker(
          response.data?.mqtt_default_broker_address || FALLBACK_BROKER,
        );
        setDefaultTopic(response.data?.mqtt_default_topic || FALLBACK_TOPIC);
        setUsingDefaults(!broker && !topic);
      } else {
        message.error(response.message || "載入MQTT設定失敗");
      }
    } catch (error) {
      message.error("載入MQTT設定失敗");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const response = await IndoorPositioning.UpdateIndoorPositioning(
        machineID,
        {
          mqtt_broker_address: values.mqtt_broker_address,
          mqtt_topic: values.mqtt_topic,
        },
      );
      if (response.code === 200) {
        message.success("MQTT設定已儲存");
        onClose();
      } else {
        message.error(response.message || "儲存MQTT設定失敗");
      }
    } catch (error) {
      if (error.errorFields) return;
      message.error("儲存MQTT設定失敗");
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    form.resetFields();
    onClose();
  };

  const fillDefaults = () => {
    form.setFieldsValue({
      mqtt_broker_address: defaultBroker,
      mqtt_topic: defaultTopic,
    });
    setUsingDefaults(false);
  };

  const handleFieldChange = () => {
    const broker = form.getFieldValue("mqtt_broker_address");
    const topic = form.getFieldValue("mqtt_topic");
    setUsingDefaults(!broker && !topic);
  };

  const status = STATUS_CONFIG[mqttStatus] || STATUS_CONFIG["未連線"];

  return (
    <Modal
      title="MQTT設定"
      open={open}
      onCancel={handleClose}
      onOk={handleSave}
      confirmLoading={saving}
      width={600}
    >
      <Spin spinning={loading}>
        <div
          style={{
            marginBottom: 16,
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <Tag
            color={status.color}
            style={{ margin: 0, fontSize: 14, padding: "2px 10px" }}
          >
            {status.label}
          </Tag>
          <Typography.Text type="secondary">
            {status.description}
          </Typography.Text>
        </div>

        {usingDefaults && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="目前使用系統預設Broker"
            description={
              <span>
                未設定自訂Broker時，系統將自動使用預設位址{" "}
                <Typography.Text code>{defaultBroker}</Typography.Text> 及Topic{" "}
                <Typography.Text code>{defaultTopic}</Typography.Text>。
                <Typography.Link
                  onClick={fillDefaults}
                  style={{ marginLeft: 8 }}
                >
                  填入預設值
                </Typography.Link>
              </span>
            }
          />
        )}

        <Form form={form} layout="vertical" onValuesChange={handleFieldChange}>
          <Form.Item
            label="Broker地址"
            name="mqtt_broker_address"
            tooltip={`系統預設：${defaultBroker}`}
          >
            <Input placeholder={defaultBroker} />
          </Form.Item>
          <Form.Item
            label="Topic"
            name="mqtt_topic"
            tooltip={`系統預設：${defaultTopic}`}
          >
            <Input placeholder={defaultTopic} />
          </Form.Item>
        </Form>
      </Spin>
    </Modal>
  );
};

export default MqttModal;
